import api from '../utils/api';
import {
  Subscriber,
  SubscriberStats,
  Blog,
  BlogStats,
  Campaign,
  CampaignStats,
  GenerateBlogRequest,
  GenerateCampaignRequest,
  GeneratedCampaignContent,
  ApiResponse
} from '../types/marketing.types';

// Subscriber API
export const subscriberApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    tag?: string;
    search?: string;
  }): Promise<ApiResponse<Subscriber[]>> => {
    const response = await api.get('/subscribers', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Subscriber>> => {
    const response = await api.get(`/subscribers/${id}`);
    return response.data;
  },

  create: async (data: Partial<Subscriber>): Promise<ApiResponse<Subscriber>> => {
    const response = await api.post('/subscribers', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Subscriber>): Promise<ApiResponse<Subscriber>> => {
    const response = await api.put(`/subscribers/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/subscribers/${id}`);
    return response.data;
  },

  getStats: async (): Promise<ApiResponse<SubscriberStats>> => {
    const response = await api.get('/subscribers/stats');
    return response.data;
  },

  import: async (subscribers: Array<Partial<Subscriber>>): Promise<ApiResponse<{
    imported: number;
    skipped: number;
    errors: string[];
  }>> => {
    const response = await api.post('/subscribers/import', { subscribers });
    return response.data;
  },

  getByLeadId: async (leadId: string, email?: string): Promise<ApiResponse<Subscriber[]>> => {
    const params = email ? { email } : {};
    const response = await api.get(`/subscribers/by-lead/${leadId}`, { params });
    return response.data;
  },

  linkToLead: async (subscriberId: string, leadId: string): Promise<ApiResponse<Subscriber>> => {
    const response = await api.put(`/subscribers/${subscriberId}/link-lead`, { leadId });
    return response.data;
  }
};

// Blog API
export const blogApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    tag?: string;
    category?: string;
    search?: string;
    author?: string;
  }): Promise<ApiResponse<Blog[]>> => {
    const response = await api.get('/blogs', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Blog>> => {
    const response = await api.get(`/blogs/${id}`);
    return response.data;
  },

  getBySlug: async (slug: string): Promise<ApiResponse<Blog>> => {
    const response = await api.get(`/blogs/${slug}`);
    return response.data;
  },

  create: async (data: Partial<Blog>): Promise<ApiResponse<Blog>> => {
    const response = await api.post('/blogs', data);
    return response.data;
  },

  generateWithAI: async (request: GenerateBlogRequest): Promise<ApiResponse<Blog>> => {
    const response = await api.post('/blogs/generate', request);
    return response.data;
  },

  update: async (id: string, data: Partial<Blog>): Promise<ApiResponse<Blog>> => {
    const response = await api.put(`/blogs/${id}`, data);
    return response.data;
  },

  publish: async (id: string): Promise<ApiResponse<Blog>> => {
    const response = await api.put(`/blogs/${id}/publish`);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/blogs/${id}`);
    return response.data;
  },

  getStats: async (): Promise<ApiResponse<BlogStats>> => {
    const response = await api.get('/blogs/stats');
    return response.data;
  },

  incrementViewCount: async (id: string): Promise<void> => {
    await api.post(`/blogs/public/${id}/view`);
  }
};

// Campaign API
export const campaignApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    search?: string;
  }): Promise<ApiResponse<Campaign[]>> => {
    const response = await api.get('/campaigns', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Campaign>> => {
    const response = await api.get(`/campaigns/${id}`);
    return response.data;
  },

  create: async (data: Partial<Campaign>): Promise<ApiResponse<Campaign>> => {
    const response = await api.post('/campaigns', data);
    return response.data;
  },

  generateWithAI: async (request: GenerateCampaignRequest): Promise<ApiResponse<GeneratedCampaignContent>> => {
    const response = await api.post('/campaigns/generate', request);
    return response.data;
  },

  update: async (id: string, data: Partial<Campaign>): Promise<ApiResponse<Campaign>> => {
    const response = await api.put(`/campaigns/${id}`, data);
    return response.data;
  },

  getRecipientPreview: async (id: string): Promise<ApiResponse<{
    count: number;
    preview: Array<{ email: string; name: string }>;
    hasMore: boolean;
  }>> => {
    const response = await api.get(`/campaigns/${id}/recipients`);
    return response.data;
  },

  send: async (id: string): Promise<ApiResponse<Campaign>> => {
    const response = await api.post(`/campaigns/${id}/send`);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/campaigns/${id}`);
    return response.data;
  },

  getStats: async (): Promise<ApiResponse<CampaignStats>> => {
    const response = await api.get('/campaigns/stats');
    return response.data;
  }
};
